import React, { createContext, useContext, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { parse, stringify } from 'qs'

// config
import constants from 'config/constants'

const NavigationContext = createContext()

export const NavigationProvider = ({ children }) => {
  const navigate = useNavigate()

  const value = useMemo(() => ({ navigate }), [navigate])

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
}

export const useNavigation = () => useContext(NavigationContext)

// These helpers are for search filter management. Here is a list of URL parameter abbreviations:
//
// bt             - An optional beneficiary_type filter used to filter opportunities
// cid            - An optional campaign_id used to filter opportunities
// ct             - An optional category filter used to filter for opportunities
// gt             - An optional goal_type filter used to filter opportunities
// l              - An optional location search filter used to filter opportunities
// nt             - An optional filter used to filter by org_type
// oid            - An optional filter used to filter by organisation ID
// ot             - An optional org_type filter used to filter organisations
// page           - The page number of the search query
// tid            - An optional team_id used to filter people
// s              - An optional search query parameter for search input fields
// sd             - An optional start date query parameter for opportunities
// sf             - Alias for 's' which is a search query parameter for searching opportunity titles
// role           - Used on Growsmart for people filtering
// comptype       - Used on Growsmart for competitions
// eventType      - delivery type for email logs
// delivStatus    - delivery status for email logs
// storyCategory  - category_id for stories
// storyType      - story_type for stories

const scrollToResults = () => {
  const resultsContainer = document.getElementById('results')

  if (resultsContainer) {
    const y = resultsContainer.getBoundingClientRect().top + window.pageYOffset - 90
    window.scrollTo({ top: y, behavior: 'smooth' })
  }
}

const RedirectToLastPage = ({ pageCount }) => {
  const navigate = useNavigate()

  useEffect(() => {
    const urlParams = parse(window.location.search.replace('?', ''))
    const redirectPath = stringify({ ...urlParams, page: pageCount })
    navigate(`?${redirectPath}`, { replace: true })
  }, [pageCount, navigate])

  // Returns null because the component is solely for redirecting
  return null
}

const useHandlePageChange = () => {
  const navigate = useNavigate()

  const handlePageChange = ({ page }) => {
    const urlParams = parse(location.search.replace('?', ''))

    if (!page && !Number.isInteger(page)) {
      delete urlParams['page']
    } else {
      urlParams['page'] = page === 0 ? 1 : page + 1
    }

    const stringifiedParams = stringify({ ...urlParams })
    const pathToUse = stringifiedParams.length > 0 ? `?${stringifiedParams}` : ``

    scrollToResults()
    navigate(pathToUse, { replace: true })
  }

  return handlePageChange
}

const useArrowKeyNavigation = () => {
  const handlePageChange = useHandlePageChange()

  useEffect(() => {
    const handlePageChangeViaArrowKey = event => {
      const { page = '1' } = parse(window.location.search.replace('?', ''))
      const nextPage = parseInt(page, 10)
      const prevPage = nextPage - 2

      // Left arrow
      if (event.keyCode === 37 && nextPage > 1) {
        handlePageChange({ page: prevPage })
      }

      // Right arrow
      if (event.keyCode === 39) {
        handlePageChange({ page: nextPage })
      }
    }

    window.addEventListener('keydown', handlePageChangeViaArrowKey)
    return () => window.removeEventListener('keydown', handlePageChangeViaArrowKey)
  }, [handlePageChange])
}

function withArrowKeyNavigation(Component) {
  // This component wraps the original component
  return function WrappedComponent(props) {
    // Use the custom hook
    useArrowKeyNavigation()

    // Pass all props unchanged to the wrapped component
    return <Component {...props} />
  }
}

const prepareSearchParams = () => {
  const {
    delivStatus = '',
    dist = '',
    eventType = '',
    prov = '',
    sch = '',
    subj = '',
    year = constants.DEFAULT_GS_YEAR,
    page = '1',
    role = '',
    cid = '',
    s = '',
    tid = '',
    oid = '',
    ot = '',
    comptype = '',
    roundLevel = '',
    storyCategory = '',
    storyType = ''
  } = parse(location.search.replace('?', ''))
  const firstPage = 1
  const pageInt = parseInt(page, 10)
  const isNegativePage = pageInt < 1
  const boundedPage = Number.isNaN(pageInt) ? firstPage : isNegativePage ? firstPage : pageInt

  return {
    delivStatus,
    dist,
    eventType,
    page: boundedPage,
    s,
    storyCategory,
    storyType,
    oid,
    ot,
    cid,
    tid,
    role,
    comptype,
    roundLevel,
    prov,
    sch,
    subj,
    year
  }
}

const updateSearchFilter = ({ navigate, event }) => {
  const urlParams = parse(location.search.replace('?', ''))
  const newParams = stringify({ ...urlParams, s: event.target.value })
  const appendString = `?${newParams}`

  navigate(appendString)
}

const updateSearchFilterDropdown = ({ navigate, value, valType }) => {
  const valueAffectingPaging = value ? encodeURIComponent(value) : null
  const urlParams = parse(location.search.replace('?', ''))
  delete urlParams[valType]

  if (valueAffectingPaging) delete urlParams['page']
  if (valueAffectingPaging) urlParams[valType] = valueAffectingPaging

  const appendString = `?${stringify(urlParams)}`
  navigate(appendString)
}

const updateParamFilter = ({ navigate, value, valType }) => {
  const valueAffectingPaging = value ? encodeURIComponent(value) : null
  const urlParams = parse(location.search.replace('?', ''))
  delete urlParams[valType]

  if (valueAffectingPaging) delete urlParams['page']
  if (valueAffectingPaging) urlParams[valType] = valueAffectingPaging

  const appendString = `?${stringify(urlParams)}`
  navigate(appendString, { replace: true })
}

export default {
  withArrowKeyNavigation,
  useHandlePageChange,
  useArrowKeyNavigation,
  prepareSearchParams,
  updateSearchFilter,
  updateSearchFilterDropdown,
  updateParamFilter,
  RedirectToLastPage
}

export {
  withArrowKeyNavigation,
  useHandlePageChange,
  useArrowKeyNavigation,
  prepareSearchParams,
  updateSearchFilter,
  updateSearchFilterDropdown,
  updateParamFilter,
  RedirectToLastPage
}
